import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ConnectorType, FeedTokenSource, PackageFeed, SaveFeedRequest } from '@/types'

const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9._-]*$/

export interface PackageFeedDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing?: PackageFeed
  availableConnectorTypes: ConnectorType[]
  isSaving: boolean
  inlineError: string | null
  onSave: (feed: SaveFeedRequest) => Promise<void>
}

export function PackageFeedDialog({
  open, onOpenChange, editing, availableConnectorTypes, isSaving, inlineError, onSave,
}: PackageFeedDialogProps) {
  const [name, setName] = useState('')
  const [feedUrl, setFeedUrl] = useState('')
  const [username, setUsername] = useState('az')
  const [tokenSource, setTokenSource] = useState<FeedTokenSource>('Explicit')
  const [reuseConnectorType, setReuseConnectorType] = useState<ConnectorType | ''>('')
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '')
      setFeedUrl(editing?.feedUrl ?? '')
      setUsername(editing?.username ?? 'az')
      setTokenSource(editing?.tokenSource ?? 'Explicit')
      setReuseConnectorType(editing?.reuseConnectorType ?? (availableConnectorTypes[0] ?? ''))
      setToken('')
      setError(null)
    }
  }, [open, editing, availableConnectorTypes])

  const handleSubmit = async () => {
    setError(null)
    if (!NAME_PATTERN.test(name)) { setError("Name must start with a letter or '_' and may only contain letters, digits, '.', '_' and '-'."); return }
    let url: URL
    try { url = new URL(feedUrl) } catch { setError('Feed URL must be an absolute HTTPS URL.'); return }
    if (url.protocol !== 'https:') { setError('Feed URL must use HTTPS.'); return }
    if (!username.trim()) { setError('Username is required.'); return }
    if (tokenSource === 'Connector' && !reuseConnectorType) { setError('Select which connector to reuse.'); return }
    if (tokenSource === 'Explicit' && !editing && !token.trim()) { setError('Token is required.'); return }
    if (tokenSource === 'Explicit' && editing?.tokenSource === 'Connector' && !token.trim()) {
      setError('A token is required when switching to a pasted token.'); return
    }
    try {
      await onSave({
        feedType: 'NuGet',
        name, feedUrl, username, tokenSource,
        reuseConnectorType: tokenSource === 'Connector' ? (reuseConnectorType as ConnectorType) : undefined,
        token: token.trim() ? token : undefined,
      })
      onOpenChange(false)
    } catch {
      /* parent surfaces inlineError */
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Edit package feed' : 'Add package feed'}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feed-name">Name</Label>
            <Input id="feed-name" data-testid="feed-name" value={name} onChange={e => setName(e.target.value)} placeholder="client-internal" />
            <p className="text-xs text-muted-foreground">Tip: match a repo's nuget.config source name if that repo clears inherited sources.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feed-url">Feed URL (v3 index.json)</Label>
            <Input id="feed-url" data-testid="feed-url" value={feedUrl} onChange={e => setFeedUrl(e.target.value)}
              placeholder="https://pkgs.dev.azure.com/org/_packaging/feed/nuget/v3/index.json" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="feed-username">Username</Label>
            <Input id="feed-username" data-testid="feed-username" value={username} onChange={e => setUsername(e.target.value)} placeholder="az" />
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Token source</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="tokenSource" checked={tokenSource === 'Explicit'} onChange={() => setTokenSource('Explicit')} />
              Paste a token
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="tokenSource" disabled={availableConnectorTypes.length === 0}
                checked={tokenSource === 'Connector'} onChange={() => setTokenSource('Connector')} />
              Reuse a connector{availableConnectorTypes.length === 0 && ' (connect one first)'}
            </label>
          </fieldset>
          {tokenSource === 'Connector' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="feed-connector">Connector</Label>
              <select id="feed-connector" data-testid="feed-connector" className="border rounded-md p-2 text-sm"
                value={reuseConnectorType} onChange={e => setReuseConnectorType(e.target.value as ConnectorType)}>
                {availableConnectorTypes.map(ct => <option key={ct} value={ct}>{ct}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">Uses your stored {reuseConnectorType || 'connector'} token; we verify it can read the feed before saving.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="feed-token">Token</Label>
              <Input id="feed-token" data-testid="feed-token" type="password" autoComplete="new-password" value={token} onChange={e => setToken(e.target.value)}
                placeholder={editing ? 'Leave blank to keep current token' : 'Personal access token'} />
            </div>
          )}
          {(error || inlineError) && <p role="alert" className="text-sm text-destructive">{error ?? inlineError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="feed-save">{isSaving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
